import { computed, onMounted, ref, onBeforeUnmount } from 'vue';

declare global {
  interface Navigator {
    standalone?: boolean;
  }

}

import shareIconSvg from '@/assets/icons/share.svg';
import downloadIconSvg from '@/assets/icons/download.svg';

export default function useInstallPrompt() {
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const showPopup = ref(false);
  const isIos = ref(false);
  const shareIcon = computed(() => shareIconSvg);
  const downloadIcon = computed(() => downloadIconSvg);

  const checkAppInstallation = () => {
    if (
      typeof window !== 'undefined' &&
      (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone)
    ) {
      showPopup.value = false;
    }
  };

  const shouldShowPopup = () => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
    const lastClosed = localStorage.getItem('notificationClosedAt');
    if (lastClosed) {
      const diffInDays = (Date.now() - Number(lastClosed)) / (1000 * 60 * 60 * 24);
      return diffInDays > 7;
    }
    return true;
  };

  const handleBeforeInstallPrompt = (e: Event) => {
    if (!isIos.value) {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
      if (shouldShowPopup()) showPopup.value = true;
    }
  };

  onMounted(() => {
    if (typeof navigator !== 'undefined') {
      isIos.value = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      checkAppInstallation();
    }
  });

  onBeforeUnmount(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }
  });

  const installApp = async () => {
    if (!deferredPrompt.value) {
      console.warn('PWA cannot install');
      return;
    }

    const prompt = deferredPrompt.value;
    deferredPrompt.value = null;

    await prompt.prompt();
    await prompt.userChoice;

    showPopup.value = false;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('notificationClosedAt', Date.now().toString());
    }
  };

  const closePopup = () => {
    showPopup.value = false;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('notificationClosedAt', Date.now().toString());
    }
  };

  return { showPopup, isIos, shareIcon, downloadIcon, installApp, closePopup };
}
